import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHotelLocation } from "@/lib/hotelLocations";

// -----------------------------------------------------------------------------
//  PropertyCardMiniMap
//  Compact, non-interactive satellite-map inset used inside the /contact and
//  /booking property cards. Shares the ArcGIS World-Imagery tile provider
//  and the terracotta-outlined pin with `HotelAboutMap` so the two surfaces
//  feel like one system.
//
//  Interaction is intentionally DISABLED on this variant — 14 maps render
//  side-by-side on the /booking grid; making each one draggable would steal
//  scroll on mobile and add significant CPU cost. Users tap the whole card
//  when they want to explore the location in full detail.
//
//  Lazy-inited via IntersectionObserver so off-screen cards defer their
//  Leaflet setup + tile downloads until they scroll into view.
// -----------------------------------------------------------------------------

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  location: { en: "Location", fr: "Emplacement", es: "Ubicación" },
  coming: { en: "Location coming soon", fr: "Localisation à venir", es: "Ubicación próximamente" },
  // "Open map" — used on the card variant (button that opens the
  // enlarged location modal). Semantically fits the action.
  openMap: { en: "Open map", fr: "Ouvrir la carte", es: "Abrir mapa" },
  // "Open route" — used on the default variant (link that jumps
  // directly to Google Maps directions). Preserved on /booking,
  // /contact, and inside the PropertyLocationModal itself.
  openRoute: { en: "Open route", fr: "Itinéraire", es: "Abrir ruta" },
};

// Morocco centroid → used to detect "no per-slug coords yet".
const MOROCCO_CENTROID = [31.7917, -7.0926];

// Reuse the exact pin visual from HotelAboutMap so both surfaces read as one
// piece of art direction. Small-scale so it sits well inside a 16/9 tile.
const PIN_ICON = L.divIcon({
  className: "xaluca-card-pin",
  html: `
    <div style="position:relative;width:22px;height:22px;">
      <span style="
        position:absolute;inset:0;border-radius:50%;
        background:#C16542;
        box-shadow:0 0 0 3px #FDFBF7, 0 4px 10px rgba(0,0,0,0.45);
      "></span>
      <span style="
        position:absolute;left:50%;top:50%;width:6px;height:6px;border-radius:50%;
        background:#FDFBF7;transform:translate(-50%,-50%);
      "></span>
    </div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Zoom level per category — refuges + bivouacs sit further from population
// so a slightly wider zoom gives useful surrounding context. Hotels are
// near towns so we can go tighter.
const CATEGORY_ZOOM = {
  hotel: 13,
  refuge: 12,
  bivouac: 11,
};

const PropertyCardMiniMap = ({
  hotelSlug,
  hotelName,
  hotelLocation,
  hotelCategory,
  aspectRatio = "16/9",
  className = "",
  onOpenModal,
}) => {
  const { lang } = useLanguage();
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const { coords, hasRealCoords, address, gmapsUrl } = useMemo(() => {
    const loc = getHotelLocation(hotelSlug, { location: hotelLocation });
    const c = loc.coords;
    const isFallback =
      Array.isArray(c) &&
      c[0] === MOROCCO_CENTROID[0] &&
      c[1] === MOROCCO_CENTROID[1];
    const dest = encodeURIComponent(`${hotelName} — ${pick(loc.address, lang)}`);
    return {
      coords: c,
      hasRealCoords: !!c && !isFallback,
      address: pick(loc.address, lang),
      gmapsUrl:
        c && !isFallback
          ? `https://www.google.com/maps/dir/?api=1&destination=${c[0]},${c[1]}&destination_place_id=${dest}`
          : null,
    };
  }, [hotelSlug, hotelName, hotelLocation, lang]);

  // Placeholder — never a broken tile. Cards with unknown coordinates fall
  // back to a soft cream tile with the "location coming soon" label.
  if (!hasRealCoords) {
    return (
      <div
        ref={wrapRef}
        className={`relative w-full max-w-full overflow-hidden border border-[#E8DFCF] bg-[#F2EBE1]/60 flex flex-col items-center justify-center px-4 text-center aspect-[${aspectRatio}] ${className}`}
        data-testid={`card-mini-map-${hotelSlug}-placeholder`}
      >
        <MapPin className="w-4 h-4 text-[#5C5248]/60 mb-1" strokeWidth={1.5} />
        <p className="text-[9px] tracking-[0.24em] uppercase text-[#5C5248]/75">
          {pick(COPY.coming, lang)}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className={`relative w-full max-w-full overflow-hidden border border-[#E8DFCF] bg-[#E8DFCF] aspect-[${aspectRatio}] ${className}`}
      style={{ contain: "layout paint size" }}
      data-testid={`card-mini-map-${hotelSlug}`}
      aria-label={pick(
        { en: `${hotelName} — ${address}`, fr: `${hotelName} — ${address}`, es: `${hotelName} — ${address}` },
        lang
      )}
    >
      {inView ? (
        <>
          <MapContainer
            center={coords}
            zoom={CATEGORY_ZOOM[hotelCategory] || 13}
            minZoom={5}
            maxZoom={18}
            zoomControl={false}
            attributionControl={false}
            scrollWheelZoom={false}
            dragging={false}
            doubleClickZoom={false}
            touchZoom={false}
            keyboard={false}
            className="absolute inset-0 w-full h-full pointer-events-none"
            data-testid={`card-mini-map-${hotelSlug}-leaflet`}
          >
            <TileLayer
              attribution=""
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={18}
              detectRetina
            />
            <Marker position={coords} icon={PIN_ICON} interactive={false} />
          </MapContainer>

          {/* Subtle vignette so the pin stays legible over any tile
              (desert dunes push everything toward warm tones, mountains
              toward deep greens). Kept minimal — the card body already
              carries the primary location label above the map, so no
              text overlay is needed here. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1513]/25 via-transparent to-transparent" />

          {/* Screen-reader-only address for context — visually the pin
              speaks for itself, but assistive tech needs the text hook. */}
          <span
            className="sr-only"
            data-testid={`card-mini-map-${hotelSlug}-label`}
          >
            {address}
          </span>

          {/* "Open route" pill — discreet CTA in the top-right corner.
              Two operating modes:
                • When `onOpenModal` is provided (booking-widget card
                  variant), the pill becomes a <button> that opens the
                  full-detail location modal (which itself renders
                  this same map + a "Open route" link to Google Maps
                  for the final directions handoff).
                • When `onOpenModal` is NOT provided (default — used
                  inside the modal, /booking and /contact), the pill
                  stays as an <a> jumping straight to Google Maps
                  directions in a new tab. */}
          {gmapsUrl ? (
            onOpenModal ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenModal();
                }}
                title={address}
                aria-label={`${pick(COPY.openMap, lang)} — ${hotelName}`}
                className="absolute top-2 right-2 z-[600] inline-flex items-center gap-1 px-2 py-1 bg-[#C16542]/90 hover:bg-[#A35133] text-[#FDFBF7] text-[8px] md:text-[9px] tracking-[0.2em] uppercase backdrop-blur-sm shadow-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDFBF7] pointer-events-auto"
                data-testid={`card-mini-map-${hotelSlug}-open`}
              >
                <ExternalLink className="w-2.5 h-2.5" strokeWidth={2} />
                <span>{pick(COPY.openMap, lang)}</span>
              </button>
            ) : (
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={address}
                aria-label={`${pick(COPY.openRoute, lang)} — ${hotelName}`}
                className="absolute top-2 right-2 z-[600] inline-flex items-center gap-1 px-2 py-1 bg-[#C16542]/90 hover:bg-[#A35133] text-[#FDFBF7] text-[8px] md:text-[9px] tracking-[0.2em] uppercase backdrop-blur-sm shadow-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDFBF7] pointer-events-auto"
                data-testid={`card-mini-map-${hotelSlug}-open`}
              >
                <ExternalLink className="w-2.5 h-2.5" strokeWidth={2} />
                <span>{pick(COPY.openRoute, lang)}</span>
              </a>
            )
          ) : null}
        </>
      ) : (
        <div className="absolute inset-0 bg-[#E8DFCF]" aria-hidden="true" />
      )}
    </div>
  );
};

export default PropertyCardMiniMap;
